import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatRelative } from "../lib/api";
import Layout from "../components/Layout";
import { Heart, Sparkles, Camera, ShoppingBag, Plus, Lock, Rocket } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ICONS = {
  sex_treff: Heart,
  spesialinteresser: Sparkles,
  tjenester: Camera,
  marked: ShoppingBag,
};

export default function Annonser() {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState({ categories: [], can_post: false });
  const [spotlight, setSpotlight] = useState([]);
  const [loading, setLoading] = useState(true);

  // Slå opp oversatt etikett, ellers fallback til server-svar
  const catLabel = (c) => i18n.exists(`ads.category.${c.key}`) ? t(`ads.category.${c.key}`) : c.label;
  const catBlurb = (c) => i18n.exists(`ads.category_desc.${c.key}`) ? t(`ads.category_desc.${c.key}`) : c.blurb;
  const subLabel = (s) => i18n.exists(`ads.sub.${s.key}`) ? t(`ads.sub.${s.key}`) : s.label;

  useEffect(() => {
    Promise.all([
      api.get("/ads/categories").catch(() => ({ data: { categories: [], can_post: false } })),
      api.get("/ads/spotlight?limit=3").catch(() => ({ data: { results: [] } })),
    ]).then(([cat, spot]) => {
      setData(cat.data);
      setSpotlight(spot.data.results || []);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <Layout withSidebar={false}>
      <div className="flex items-start justify-between mb-4 sm:mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-fraunces italic text-2xl sm:text-5xl text-[var(--cream)] leading-tight">
            {t("ads.page_title")}.
          </h1>
          <p className="font-manrope text-[var(--cream-soft)] mt-2 max-w-2xl text-xs sm:text-sm leading-relaxed hidden sm:block">
            {t("ads.page_subtitle")}
          </p>
        </div>
        {data.can_post ? (
          <Link to="/annonser/ny" className="btn-magenta inline-flex items-center" data-testid="new-ad-btn">
            <Plus className="h-3.5 w-3.5 mr-2" /> {t("ads.publish_ad").replace(/^\+\s*/, "")}
          </Link>
        ) : (
          <Link to="/oppgrader" className="btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="upgrade-prompt">
            <Lock className="h-3 w-3 text-[var(--cognac)]" /> {t("ads.become_vip_to_publish")}
          </Link>
        )}
      </div>

      {/* Spotlight – boostede annonser */}
      {spotlight.length > 0 && (
        <section className="mb-10" data-testid="spotlight">
          <div className="flex items-center gap-2 mb-3">
            <Rocket className="h-4 w-4 text-[var(--magenta)]" />
            <h2 className="font-fraunces italic text-xl text-[var(--cream)]">{t("ads.current_ads_now")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {spotlight.map((ad) => {
              const img = ad.image_paths?.[0];
              return (
                <Link key={ad.id} to={`/annonser/se/${ad.id}`}
                      className="relative border border-[var(--magenta)]/40 bg-[var(--magenta)]/[0.04] hover:bg-[var(--magenta)]/[0.08] transition-colors flex"
                      data-testid={`spotlight-${ad.id}`}>
                  <div className="w-32 aspect-square bg-black flex items-center justify-center overflow-hidden shrink-0">
                    {img ? (
                      <img src={`${BACKEND}/api/ads/images/raw?path=${encodeURIComponent(img)}`}
                           alt={ad.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="font-fraunces italic text-[var(--cream-faint)] text-xs">–</div>
                    )}
                  </div>
                  <div className="p-3 flex-1 min-w-0">
                    <div className="font-meta text-[9px] inline-flex items-center gap-1 text-[var(--magenta)] mb-1">
                      <Rocket className="h-2.5 w-2.5" /> BOOST
                    </div>
                    <div className="font-fraunces italic text-base text-[var(--cream)] line-clamp-2 leading-tight">{ad.title}</div>
                    <div className="font-meta text-[10px] mt-1 truncate">{ad.subcategory_label}</div>
                    {ad.price != null && <div className="font-fraunces italic text-lg text-[var(--magenta)] mt-1">{ad.price} kr</div>}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {loading ? (
        <div className="font-fraunces italic text-[var(--cream-soft)]">{t("common.loading")}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.categories.map((c) => {
            const Icon = ICONS[c.key] || Heart;
            return (
              <Link key={c.key} to={`/annonser/${c.key}`} data-testid={`cat-${c.key}`}
                    className="border border-white/10 rounded-sm p-3 sm:p-5 bg-[var(--noir-elev)]/30 hover:bg-[var(--noir-elev)]/60 hover:border-[var(--magenta)]/50 transition-colors">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-sm bg-[var(--magenta)]/15 flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-[var(--magenta)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <h2 className="font-fraunces italic text-lg sm:text-2xl text-[var(--cream)]">{catLabel(c)}</h2>
                      <span className="font-meta text-[10px] text-[var(--cream-soft)]">{c.total}</span>
                    </div>
                    <p className="font-manrope text-xs text-[var(--cream-soft)] mt-1 leading-relaxed line-clamp-2 sm:line-clamp-none">{catBlurb(c)}</p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {c.subs.map((s) => (
                        <Link key={s.key} to={`/annonser/${c.key}/${s.key}`}
                              onClick={(e) => e.stopPropagation()}
                              className="font-meta text-[10px] px-2 sm:px-2.5 py-0.5 sm:py-1 border border-white/15 rounded-sm text-[var(--cream-soft)] hover:border-[var(--magenta)]/60 hover:text-[var(--magenta)] transition-colors"
                              data-testid={`sub-${c.key}-${s.key}`}>
                          {subLabel(s)} <span className="text-[var(--cream-faint)]">({s.count})</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
